import { Card, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, Thead, Tbody, Tr, Th, Td } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { useOnlineUsers, useUptime } from '../../hooks/use-activity';
import { timeAgo } from '../../lib/utils';

const ROLE_COLORS: Record<string, string> = {
  CLIENT: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  AGENT: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  ADMIN: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
};

const ROLE_LABELS: Record<string, string> = { CLIENT: 'Client', AGENT: 'Agent', ADMIN: 'Admin' };

const PAGE_LABELS: Record<string, string> = {
  '/': 'Acasă',
  '/tickets': 'Tichete',
  '/tickets/new': 'Tichet nou',
  '/profile': 'Profil',
  '/admin': 'Dashboard',
  '/admin/tickets': 'Tichete (admin)',
  '/admin/clients': 'Clienți',
  '/admin/users': 'Utilizatori',
  '/admin/sla': 'SLA',
  '/admin/tags': 'Etichete',
  '/admin/webcon': 'WebCon',
  '/admin/microsoft': 'Microsoft',
  '/admin/audit': 'Audit',
  '/admin/settings': 'Setări',
  '/admin/activity': 'Activitate',
};

function getPageLabel(path: string): string {
  // Exact match
  if (PAGE_LABELS[path]) return PAGE_LABELS[path];
  // Pattern match for ticket detail pages
  if (/^\/admin\/tickets\/[^/]+$/.test(path)) return 'Detalii tichet (admin)';
  if (/^\/tickets\/[^/]+$/.test(path)) return 'Detalii tichet';
  if (/^\/admin\/clients\/[^/]+$/.test(path)) return 'Detalii client';
  return path;
}

function formatDuration(lastSeen: string): string {
  const ms = Date.now() - new Date(lastSeen).getTime();
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return 'acum';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

export function ActivityDashboardPage() {
  const { data: onlineData, isLoading: loadingUsers } = useOnlineUsers();
  const { data: uptimeData, isLoading: loadingUptime } = useUptime();

  const users = onlineData?.data ?? [];
  const uptime = uptimeData?.data;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">Activitate în timp real</h1>

      {/* Uptime card */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="p-4">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-dark-muted">Utilizatori online</p>
          <p className="mt-1 text-3xl font-bold text-primary">{loadingUsers ? '—' : users.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-dark-muted">Uptime platformă</p>
          <p className="mt-1 text-3xl font-bold text-green-600">{loadingUptime ? '—' : uptime?.formatted ?? '—'}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-dark-muted">Pornit la</p>
          <p className="mt-1 text-lg font-semibold text-gray-700 dark:text-dark-text">
            {loadingUptime ? '—' : uptime?.startedAt ? new Date(uptime.startedAt).toLocaleString('ro-RO') : '—'}
          </p>
        </Card>
      </div>

      {/* Online users table */}
      <Card>
        <CardHeader>
          <h2 className="font-semibold text-gray-900 dark:text-dark-text">Utilizatori conectați</h2>
        </CardHeader>
        <Table>
          <Thead>
            <Tr>
              <Th>Nume</Th>
              <Th>Email</Th>
              <Th>Rol</Th>
              <Th>Pagina curentă</Th>
              <Th>Ultima activitate</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loadingUsers && Array.from({ length: 3 }).map((_, i) => (
              <Tr key={i}><Td colSpan={5}><Skeleton className="h-4 w-full" /></Td></Tr>
            ))}
            {!loadingUsers && users.length === 0 && (
              <Tr><Td colSpan={5} className="text-center text-gray-400">Niciun utilizator conectat</Td></Tr>
            )}
            {users.map((u) => (
              <Tr key={u.userId}>
                <Td className="font-medium text-gray-900 dark:text-dark-text">{u.name}</Td>
                <Td className="text-sm text-gray-500">{u.email}</Td>
                <Td><Badge className={ROLE_COLORS[u.role] ?? ''}>{ROLE_LABELS[u.role] ?? u.role}</Badge></Td>
                <Td className="text-sm text-gray-600 dark:text-dark-muted">{getPageLabel(u.page)}</Td>
                <Td className="text-sm text-gray-400">{formatDuration(u.lastSeen)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Card>
    </div>
  );
}
